// src/lib/supabase.js
// Lightweight REST client for the Closet Collective Supabase backend.
// Tables: members, listings, payout_splits (+ view v_member_earnings).

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const headers = {
  "Content-Type": "application/json",
  "apikey": SUPABASE_ANON_KEY,
  "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
  "Prefer": "return=representation",
};

const ready = () => Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// ── Listings ──────────────────────────────────────────────────────────────────
export async function fetchListings() {
  if (!ready()) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/listings?order=created_at.desc&limit=200`,
    { headers }
  );
  if (!res.ok) return [];
  return res.json();
}

export async function saveListing(listing) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/listings`, {
    method: "POST",
    headers,
    body: JSON.stringify({ ...listing, status: "available" }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

// Update the status of a listing: 'available' | 'sold' | 'cancelled'
export async function updateStatus(id, status) {
  if (!ready()) return;
  const sold = status === "sold";
  await fetch(`${SUPABASE_URL}/rest/v1/listings?id=eq.${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ status, sold }),
  });
}

// Mark a listing sold WITH economics (actual sale price, fees, shipping).
export async function recordSale(id, { sale_price, platform_fee, shipping_cost }) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/listings?id=eq.${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({
      status: "sold",
      sold: true,
      sale_price: sale_price ?? null,
      platform_fee: platform_fee ?? 0,
      shipping_cost: shipping_cost ?? 0,
    }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

// Hard delete a listing permanently (cascades to its payout splits)
export async function deleteListing(id) {
  if (!ready()) return;
  await fetch(`${SUPABASE_URL}/rest/v1/listings?id=eq.${id}`, {
    method: "DELETE",
    headers: { ...headers, Prefer: "" },
  });
}

// Legacy export kept for any existing references
export async function markSold(id, sold) {
  return updateStatus(id, sold ? "sold" : "available");
}

// ── Members ───────────────────────────────────────────────────────────────────
export async function fetchMembers() {
  if (!ready()) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/members?is_active=eq.true&order=name.asc`,
    { headers }
  );
  if (!res.ok) return [];
  return res.json();
}

export async function addMember({ name, venmo_handle }) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/members`, {
    method: "POST",
    headers,
    body: JSON.stringify({ name, venmo_handle: venmo_handle || null }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

// All members incl. inactive (admin view)
export async function fetchAllMembers() {
  if (!ready()) return [];
  const res = await fetch(`${SUPABASE_URL}/rest/v1/members?order=name.asc`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function updateMember(id, fields) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/members?id=eq.${id}`, {
    method: "PATCH", headers, body: JSON.stringify(fields),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

// Hard delete a member (their listings/payouts keep history via ON DELETE SET NULL)
export async function deleteMember(id) {
  if (!ready()) return;
  await fetch(`${SUPABASE_URL}/rest/v1/members?id=eq.${id}`, {
    method: "DELETE", headers: { ...headers, Prefer: "" },
  });
}

// Generic listing field update (admin edits: status, prices, seller, etc.)
export async function updateListing(id, fields) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/listings?id=eq.${id}`, {
    method: "PATCH", headers, body: JSON.stringify(fields),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

export async function updatePayoutSplit(id, fields) {
  if (!ready()) return null;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/payout_splits?id=eq.${id}`, {
    method: "PATCH", headers, body: JSON.stringify(fields),
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data[0] || null;
}

export async function deletePayoutSplit(id) {
  if (!ready()) return;
  await fetch(`${SUPABASE_URL}/rest/v1/payout_splits?id=eq.${id}`, {
    method: "DELETE", headers: { ...headers, Prefer: "" },
  });
}

// ── Payout splits ─────────────────────────────────────────────────────────────
// splits: [{ listing_id, member_id, member_name, split_pct, amount }]
export async function savePayoutSplits(splits) {
  if (!ready() || !splits?.length) return [];
  const res = await fetch(`${SUPABASE_URL}/rest/v1/payout_splits`, {
    method: "POST",
    headers,
    body: JSON.stringify(splits),
  });
  if (!res.ok) return [];
  return res.json();
}

// Remove existing splits for a listing (used when re-opening / re-recording)
export async function clearPayoutSplits(listingId) {
  if (!ready()) return;
  await fetch(`${SUPABASE_URL}/rest/v1/payout_splits?listing_id=eq.${listingId}`, {
    method: "DELETE",
    headers: { ...headers, Prefer: "" },
  });
}

export async function fetchPayoutSplits() {
  if (!ready()) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/payout_splits?order=created_at.desc`,
    { headers }
  );
  if (!res.ok) return [];
  return res.json();
}

export async function markSplitPaid(id, paid) {
  if (!ready()) return;
  await fetch(`${SUPABASE_URL}/rest/v1/payout_splits?id=eq.${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ paid, paid_at: paid ? new Date().toISOString() : null }),
  });
}

// Settle every outstanding split for one member at once
export async function markMemberPaid(memberId) {
  if (!ready() || !memberId) return;
  await fetch(`${SUPABASE_URL}/rest/v1/payout_splits?member_id=eq.${memberId}&paid=eq.false`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ paid: true, paid_at: new Date().toISOString() }),
  });
}

// Per-member earnings rollup (from the v_member_earnings view)
export async function fetchMemberEarnings() {
  if (!ready()) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/v_member_earnings?order=unpaid_owed.desc`,
    { headers }
  );
  if (!res.ok) return [];
  return res.json();
}
